import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Button from './Button';
import Input from './Input';
import Loader from './Loader';
import ActivityHeatmap from './ActivityHeatmap';
import { COMMUNITY_ADMIN_PATH } from '../../AdminDashboards/adminTools';

const PURCHASE_DAYS = 5;
const HEATMAP_MONTHS = 5;
const USERS_PER_PAGE = 20;
// Buyers one signals request asks about, kept small so it never times out
const SIGNALS_BATCH = 3;

const GET_PURCHASES_OVERVIEW = gql`
  query getPurchasesOverview(
    $days: Int
    $months: Int
    $usersPage: Int
    $usersLimit: Int
    $showAll: Boolean
    $purchaseId: String
  ) {
    getPurchasesOverview(
      days: $days
      months: $months
      usersPage: $usersPage
      usersLimit: $usersLimit
      showAll: $showAll
      purchaseId: $purchaseId
    ) {
      showAll
      purchaseId
      purchasesFrom
      to
      totalPurchases
      totalUsers
      usersPage
      usersLimit
      users {
        id
        email
        nickname
        purchasesInPeriod
        lastPurchaseAt
        shopItems {
          id
          name
          tokenPrice
          count
        }
      }
    }
  }
`;

// The activity and the signals of those buyers, each on its own request so a
// slow one does not hold up the list
const GET_ACTIVITY = gql`
  query getPurchasesOverviewActivity($userIds: [ID!]!, $months: Int) {
    getPurchasesOverviewActivity(userIds: $userIds, months: $months) {
      heatmapFrom
      to
      users {
        id
        totalResults
        days {
          date
          count
        }
      }
    }
  }
`;

const GET_SIGNALS = gql`
  query getPurchasesOverviewSignals(
    $userIds: [ID!]!
    $days: Int
    $showAll: Boolean
    $purchaseId: String
  ) {
    getPurchasesOverviewSignals(
      userIds: $userIds
      days: $days
      showAll: $showAll
      purchaseId: $purchaseId
    ) {
      to
      users {
        id
        reviewSignals {
          kind
          level
          label
        }
        savingGroupMembers {
          id
          email
          nickname
          createdAt
        }
      }
    }
  }
`;

const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const formatDate = value =>
  value ? new Date(value).toLocaleDateString() : '-';

function PurchasesOverview() {
  const [page, setPage] = useState(0);
  // Purchases waiting to be reviewed only, until the checkbox says otherwise
  const [showAll, setShowAll] = useState(false);
  // One purchase at a time, when an id is filled in
  const [purchaseIdInput, setPurchaseIdInput] = useState('');
  const [purchaseId, setPurchaseId] = useState('');

  const { data, loading, error } = useQuery(GET_PURCHASES_OVERVIEW, {
    fetchPolicy: 'network-only',
    variables: {
      days: PURCHASE_DAYS,
      usersPage: page,
      usersLimit: USERS_PER_PAGE,
      showAll,
      purchaseId: purchaseId || null
    }
  });

  const overviewUsers =
    (data && data.getPurchasesOverview && data.getPurchasesOverview.users) ||
    [];
  const userIds = overviewUsers.map(user => user.id);

  const activityQuery = useQuery(GET_ACTIVITY, {
    fetchPolicy: 'network-only',
    skip: !userIds.length,
    variables: { userIds, months: HEATMAP_MONTHS }
  });

  const client = useApolloClient();
  const [signalsByUser, setSignalsByUser] = useState({});
  const [signalsPending, setSignalsPending] = useState(0);
  const [signalsError, setSignalsError] = useState(null);
  const [signalsKey, setSignalsKey] = useState(null);
  const signalsRun = useRef(0);

  const userIdsKey = userIds.join(',');

  useEffect(
    () => {
      signalsRun.current += 1;
      const run = signalsRun.current;

      setSignalsByUser({});
      setSignalsError(null);
      setSignalsPending(userIds.length);
      setSignalsKey(userIdsKey);

      if (!userIds.length) return undefined;

      const batches = [];
      for (let index = 0; index < userIds.length; index += SIGNALS_BATCH) {
        batches.push(userIds.slice(index, index + SIGNALS_BATCH));
      }

      // One batch at a time: the page fills in while the server only ever
      // works on a few buyers
      const loadBatches = async () => {
        // eslint-disable-next-line no-restricted-syntax
        for (const batch of batches) {
          if (signalsRun.current !== run) return;

          try {
            // eslint-disable-next-line no-await-in-loop
            const { data: batchData } = await client.query({
              query: GET_SIGNALS,
              fetchPolicy: 'network-only',
              variables: {
                userIds: batch,
                days: PURCHASE_DAYS,
                showAll,
                purchaseId: purchaseId || null
              }
            });

            if (signalsRun.current !== run) return;

            const loaded = batchData.getPurchasesOverviewSignals.users;
            setSignalsByUser(current =>
              loaded.reduce(
                (acc, user) => ({ ...acc, [user.id]: user }),
                current
              )
            );
          } catch (batchError) {
            if (signalsRun.current !== run) return;
            setSignalsError(batchError);
          }

          setSignalsPending(current => Math.max(current - batch.length, 0));
        }
      };

      loadBatches();

      return () => {
        signalsRun.current += 1;
      };
    },
    [userIdsKey, showAll, purchaseId]
  );

  const activityData =
    activityQuery.data && activityQuery.data.getPurchasesOverviewActivity;

  const signalsDone = signalsKey === userIdsKey && signalsPending === 0;
  const signalsTotal = userIds.length;
  const signalsLoaded = Math.max(signalsTotal - signalsPending, 0);

  /*
   * The page is shown in one piece: a half filled table reads as if the
   * numbers were missing instead of still on their way
   */
  const pageLoading =
    loading ||
    (!!userIds.length &&
      (activityQuery.loading || !activityData || !signalsDone));

  const loadingDetail = () => {
    if (loading || !userIds.length) return 'Loading the purchases…';
    if (activityQuery.loading || !activityData) return 'Loading the activity…';
    return `Looking for signals, ${signalsLoaded} of ${signalsTotal} buyers…`;
  };

  const activityOf = userId => {
    const activity =
      activityQuery.data && activityQuery.data.getPurchasesOverviewActivity;
    if (!activity) return null;
    return activity.users.find(user => user.id === userId) || null;
  };

  const signalsOf = userId => signalsByUser[userId] || null;

  const heatmapWindow = () => {
    const activity =
      activityQuery.data && activityQuery.data.getPurchasesOverviewActivity;
    return activity
      ? { from: new Date(activity.heatmapFrom), to: new Date(activity.to) }
      : null;
  };

  const applyPurchaseId = () => {
    setPage(0);
    setPurchaseId(purchaseIdInput.trim());
  };

  const clearPurchaseId = () => {
    setPage(0);
    setPurchaseIdInput('');
    setPurchaseId('');
  };

  const overview = data && data.getPurchasesOverview;
  const users = (overview && overview.users) || [];
  const totalUsers = (overview && overview.totalUsers) || 0;
  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE) || 1;

  const firstOnPage = totalUsers ? page * USERS_PER_PAGE + 1 : 0;
  const lastOnPage = Math.min((page + 1) * USERS_PER_PAGE, totalUsers);

  const pagination = (
    <div style={styles.pagination}>
      <span style={styles.paginationText}>
        {totalUsers
          ? `${firstOnPage}-${lastOnPage} of ${totalUsers}`
          : 'No buyers'}
      </span>
      <Button
        onClick={() => setPage(p => Math.max(0, p - 1))}
        disabled={page === 0 || pageLoading}
      >
        Previous
      </Button>
      <span style={styles.paginationText}>
        Page {page + 1} of {totalPages}
      </span>
      <Button
        onClick={() => setPage(p => p + 1)}
        disabled={page + 1 >= totalPages || pageLoading}
      >
        Next
      </Button>
    </div>
  );

  return (
    <div style={styles.container}>
      <Link to={COMMUNITY_ADMIN_PATH} style={styles.backLink}>
        &larr; Back to dashboard
      </Link>
      <h1>Purchases overview</h1>
      <p style={styles.intro}>
        {purchaseId ? 'The buyer of one purchase' : null}
        {!purchaseId && showAll
          ? `Every user that bought something in the last ${PURCHASE_DAYS} days`
          : null}
        {!purchaseId && !showAll
          ? 'Users with a purchase in review, however old it is (details filled in, not validated yet)'
          : null}
        , with the survey results they created over the last {HEATMAP_MONTHS}{' '}
        months.
      </p>

      <div style={styles.filters}>
        <label style={styles.showAll} htmlFor="purchases-show-all">
          <input
            id="purchases-show-all"
            type="checkbox"
            checked={showAll}
            disabled={!!purchaseId}
            onChange={event => {
              setPage(0);
              setShowAll(event.target.checked);
            }}
          />
          Show all purchases, also the ones that are not in review
        </label>

        <Input
          type="text"
          value={purchaseIdInput}
          onChange={event => setPurchaseIdInput(event.target.value)}
          onKeyPress={event => {
            if (event.key === 'Enter') applyPurchaseId();
          }}
          placeholder="Purchase id"
          style={styles.purchaseIdInput}
        />
        <Button onClick={applyPurchaseId} primary>
          Filter
        </Button>
        {purchaseId ? <Button onClick={clearPurchaseId}>Clear</Button> : null}
      </div>

      {error ? <p style={styles.error}>{error.message}</p> : null}

      {overview ? (
        <>
          <div style={styles.header}>
            <h2 style={styles.sectionTitle}>
              Buyers{' '}
              <span style={styles.count}>
                {totalUsers} user{totalUsers === 1 ? '' : 's'} ·{' '}
                {overview.totalPurchases} purchase
                {overview.totalPurchases === 1 ? '' : 's'}{' '}
                {purchaseId ? 'with this id' : null}
                {!purchaseId && showAll
                  ? `in the last ${PURCHASE_DAYS} days`
                  : null}
                {!purchaseId && !showAll ? 'in review' : null}
              </span>
            </h2>
            {pagination}
          </div>

          {pageLoading ? (
            <div style={styles.loading}>
              <Loader />
              <div style={styles.loadingDetail}>{loadingDetail()}</div>
            </div>
          ) : null}

          {!pageLoading && !users.length ? (
            <p style={styles.empty}>
              {purchaseId ? 'No purchase with this id.' : null}
              {!purchaseId && showAll
                ? `No purchases in the last ${PURCHASE_DAYS} days.`
                : null}
              {!purchaseId && !showAll
                ? 'No purchases are waiting for a review.'
                : null}
            </p>
          ) : null}

          {!pageLoading
            ? users.map(user => {
                const userActivity = activityOf(user.id);
                const userSignals = signalsOf(user.id);
                const window = heatmapWindow();

                return (
                  <div key={user.id} style={styles.userRow}>
                    <div style={styles.userInfo}>
                      <div style={styles.userName}>
                        {user.nickname || 'No name'}
                      </div>
                      <Link
                        to={`${COMMUNITY_ADMIN_PATH}/purchases-overview/${
                          user.id
                        }`}
                        style={styles.userEmail}
                      >
                        {user.email || user.id}
                      </Link>
                      <div style={styles.userMeta}>
                        {user.purchasesInPeriod} purchase
                        {user.purchasesInPeriod === 1 ? '' : 's'}
                        {showAll ? '' : ' in review'} · last{' '}
                        {formatDateTime(user.lastPurchaseAt)}
                      </div>

                      <Link
                        to={`${COMMUNITY_ADMIN_PATH}/purchases-overview/${
                          user.id
                        }/review`}
                        style={styles.reviewButton}
                      >
                        Review purchase
                      </Link>

                      {!userSignals && signalsError ? (
                        <div style={styles.pending}>{signalsError.message}</div>
                      ) : null}

                      {userSignals &&
                      userSignals.reviewSignals &&
                      userSignals.reviewSignals.length ? (
                        <div style={styles.signals}>
                          {userSignals.reviewSignals.map(signal => (
                            <span
                              key={signal.kind}
                              style={{
                                ...styles.signal,
                                ...(signal.level === 'alert'
                                  ? styles.signalAlert
                                  : styles.signalWarning)
                              }}
                            >
                              {signal.level === 'alert' ? '⚠' : 'ℹ'}{' '}
                              {signal.label}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {userSignals &&
                      userSignals.savingGroupMembers &&
                      userSignals.savingGroupMembers.length ? (
                        <div style={styles.groupMembers}>
                          <div style={styles.groupMembersTitle}>
                            Saving group
                          </div>
                          {userSignals.savingGroupMembers.map(member => (
                            <div key={member.id} style={styles.groupMember}>
                              <Link
                                to={`${COMMUNITY_ADMIN_PATH}/purchases-overview/${
                                  member.id
                                }`}
                                style={styles.groupMemberEmail}
                              >
                                {member.email || member.nickname || member.id}
                              </Link>
                              <span style={styles.groupMemberCreated}>
                                created {formatDate(member.createdAt)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div style={styles.userGraph}>
                      {window && userActivity ? (
                        <ActivityHeatmap
                          days={userActivity.days}
                          from={window.from}
                          to={window.to}
                          label={`${userActivity.totalResults} result${
                            userActivity.totalResults === 1 ? '' : 's'
                          } in the last ${HEATMAP_MONTHS} months`}
                        />
                      ) : (
                        <div style={styles.pending}>
                          {activityQuery.error
                            ? activityQuery.error.message
                            : 'No activity for this buyer'}
                        </div>
                      )}
                    </div>

                    <div style={styles.userItems}>
                      <div style={styles.userItemsTitle}>Purchased</div>
                      {(user.shopItems || []).map(shopItem => (
                        <div key={shopItem.id} style={styles.userItem}>
                          <span style={styles.userItemName}>
                            {shopItem.name}
                            {shopItem.count > 1 ? ` x${shopItem.count}` : ''}
                          </span>
                          <span style={styles.userItemPrice}>
                            {shopItem.tokenPrice === null
                              ? '-'
                              : shopItem.tokenPrice}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            : null}

          {!pageLoading && users.length ? (
            <div style={styles.footerPagination}>{pagination}</div>
          ) : null}
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px'
  },
  reviewButton: {
    display: 'inline-block',
    marginTop: '8px',
    padding: '6px 12px',
    borderRadius: '4px',
    background: '#007bff',
    color: 'white',
    fontSize: '12px',
    fontWeight: 600,
    textDecoration: 'none'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: '30px 0'
  },
  loadingDetail: {
    fontSize: '13px',
    color: '#57606a'
  },
  pending: {
    fontSize: '12px',
    color: '#8b949e',
    fontStyle: 'italic'
  },
  signals: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    marginTop: '8px'
  },
  signal: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1.4
  },
  signalWarning: {
    background: '#fff4e2',
    color: '#8a4b00'
  },
  signalAlert: {
    background: '#fdecea',
    color: '#c0392b'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#007bff',
    textDecoration: 'none'
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  },
  purchaseIdInput: {
    width: '260px'
  },
  showAll: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    margin: 0,
    fontSize: '13px',
    color: '#333',
    cursor: 'pointer'
  },
  intro: {
    color: '#666'
  },
  error: {
    color: '#c0392b'
  },
  empty: {
    color: '#666'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '30px',
    paddingBottom: '10px',
    borderBottom: '1px solid #eaecef'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '18px'
  },
  count: {
    fontSize: '13px',
    fontWeight: 'normal',
    color: '#666'
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  paginationText: {
    fontSize: '13px',
    color: '#666'
  },
  footerPagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px'
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '20px 0',
    borderBottom: '1px solid #eaecef'
  },
  userInfo: {
    width: '260px',
    flexShrink: 0
  },
  userName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#57606a'
  },
  userEmail: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#0969da',
    textDecoration: 'none',
    wordBreak: 'break-all'
  },
  userMeta: {
    marginTop: '6px',
    fontSize: '11px',
    color: '#8b949e'
  },
  groupMembers: {
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px dashed #d0d7de'
  },
  groupMembersTitle: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#8b949e',
    marginBottom: '4px'
  },
  groupMember: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '8px',
    fontSize: '12px',
    lineHeight: '17px'
  },
  groupMemberEmail: {
    color: '#0969da',
    textDecoration: 'none',
    wordBreak: 'break-all'
  },
  groupMemberCreated: {
    color: '#8b949e',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  userGraph: {
    flex: 1,
    minWidth: 0
  },
  userItems: {
    width: '230px',
    flexShrink: 0
  },
  userItemsTitle: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#8b949e',
    marginBottom: '6px'
  },
  userItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '10px',
    fontSize: '13px',
    lineHeight: '18px'
  },
  userItemName: {
    color: '#d73a49',
    wordBreak: 'break-word'
  },
  userItemPrice: {
    color: '#57606a',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0
  }
};

export default PurchasesOverview;
